import { FC, useCallback, useMemo } from 'react'
import { HealthInformationBlockProps } from './HealthInformationBlock.model'
import { getV3ColorCssVariable } from '../../../../design-system/colors_v3'
import { Sex } from '../../../../model/Patient'
import {
  FormBlock,
  FormikSecuriteSocialNumberInput,
  FormikSelectInput,
  TooltipWrapper,
  Icon,
  FormikTextInput,
  FormikSwitch,
} from '../../../shared'
import { DisabledINSITooltipWrapper } from '../DisabledINSITootipWrapper'
import { useFormikContext } from 'formik'
import { PatientFormFields } from '../PatientForm.model'
import { useDisableInsiFields } from '../useDisableInsiFields.hook'
import { isINSIValidated } from '../../../../misc/patient.utilities'
import { inseeNumberValidator } from '../../../../misc/inseeNumber.utils'
import styles from './HealthInformationBlock.module.scss'

const ASSIGNING_AUTHORITIES_LABEL = (
  <div className={styles.healthInformationBlockOidTooltip}>
    <span>INS : Identifiant national de santé</span>
    <span>INS-C : Identifiant national de santé calculé</span>
    <span>NIA : Numéro identifiant d'attente</span>
    <span>NIR : Numéro d'inscription au RNIPP</span>
    <span>OID : Identifiant d'objet (Object Identifier)</span>
  </div>
)

export const HealthInformationBlock: FC<HealthInformationBlockProps> = ({
  assigningAuthorities,
  hasAccessToInsOid,
  isInsiFeatureEnabled,
  isIdentityVigilanceEnabled,
}) => {
  const { values, initialValues, setValues } = useFormikContext<PatientFormFields>()

  const disableInsiFields = useDisableInsiFields() && !isIdentityVigilanceEnabled;

  const assigningAuthoritiesOptions = useMemo(() => {
    let authoritiesOptions = assigningAuthorities.map(({ oid, label }) => ({
      value: oid,
      label: `${label} (${oid})`,
    }))
    return [{ value: null, label: 'Aucun' }, ...authoritiesOptions]
  }, [assigningAuthorities])

  const findSelectedAssigningAuthorities = useCallback(
    (value?: string) =>
      assigningAuthoritiesOptions.find((authorityOption) => authorityOption.value === value) ??
      undefined,
    [assigningAuthoritiesOptions],
  )

  const handleInseeChange = useCallback(
    (value: string) => {
      let extractedBirthPlaceCode = ''
      if (inseeNumberValidator(value)) {
        extractedBirthPlaceCode = value.replaceAll(' ', '').substring(5, 10)
      }

      const birthPlaceCode =
        (values.birthPlaceCode === '99999' || !values.birthPlaceCode) && extractedBirthPlaceCode
          ? extractedBirthPlaceCode
          : values.birthPlaceCode

      setValues({
        ...values,
        inseeNumber: value,
        birthPlaceCode: birthPlaceCode,
      })
    },
    [setValues, values],
  )

  return (
    <FormBlock icon="checkup" label="Santé">
      {hasAccessToInsOid && (
        <>
          <DisabledINSITooltipWrapper display={isINSIValidated(values.identityStatus)}>
            <FormikSecuriteSocialNumberInput
              fieldName="inseeNumber"
              label="Matricule INS"
              placeholder="x xx xx xx xxx xxx xx"
              colorPreset="light"
              disabled={disableInsiFields}
              onChange={handleInseeChange}
              testId="input-patient-health_informations-inseeNumber"
            />
          </DisabledINSITooltipWrapper>
          {isInsiFeatureEnabled && (
            <div className={styles.healthInformationBlockOidContainer}>
              <DisabledINSITooltipWrapper display={isINSIValidated(values.identityStatus)}>
                <div className={styles.healthInformationBlockOidSelect}>
                  <FormikSelectInput
                    label="OID"
                    fieldName="assigningAuthority.oid"
                    placeholder="Veuillez sélectionner un OID"
                    options={assigningAuthoritiesOptions}
                    disabled={disableInsiFields}
                    value={findSelectedAssigningAuthorities(values.assigningAuthority?.oid)}
                    testId="patient-health_informations-assigning_authority_oid"
                  />
                </div>
              </DisabledINSITooltipWrapper>
              <TooltipWrapper pointerDirection="left" content={ASSIGNING_AUTHORITIES_LABEL}>
                <Icon
                  icon="infoCircle"
                  color={getV3ColorCssVariable('primary', 'default')}
                  size="normal"
                />
              </TooltipWrapper>
            </div>
          )}
        </>
      )}
      {!isIdentityVigilanceEnabled &&
      <FormikTextInput
        fieldName="healthInsuranceNumber"
        label="Assurance complémentaire"
        colorPreset="light"
        testId="input-patient-health_informations-health_insurance_number-enter"
      />}
      {!isIdentityVigilanceEnabled && (
        <div className={styles.healthInformationBlockDeceased}>
          <FormikSwitch
            name={
              values.sex === Sex.FEMALE ? 'Cette patiente est décédée' : 'Ce patient est décédé'
            }
            fieldName="isDeceased"
            borderBottom
            testId="switch-patient-health_informations-is_deceased"
          />
        </div>
      )}
      {initialValues.doctor && (
        <>
          <FormikTextInput
            fieldName="doctor"
            label="Médecin traitant *"
            colorPreset="light"
            testId="input-patient-health_informations-doctor-enter"
          />
          <div className={styles.healthInformationBlockDeprecated}>
            (*) Ce champ est obsolète. Veuillez le rechercher via les correspondants pour l'associer
            au patient.
          </div>
        </>
      )}
    </FormBlock>
  )
}
